import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listMyPurchaseOrders } from '../api/vendorApi';
import { VpSpinner, VpEmpty, VpPagination, money, fmtDate } from '../components/VpUI';

const PER_PAGE = 10;

// Approved purchase orders for the logged-in vendor. Only confirmed POs are
// returned by the backend, so every row here is an approved order.
const VendorPurchaseOrders = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [rows, setRows] = useState<any[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [error, setError] = useState('');

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const data = await listMyPurchaseOrders({
				pageNo: page,
				perPage: PER_PAGE,
				search: search || undefined,
			});
			setRows(data?.records || []);
			setTotal(data?.totalRecords || 0);
		} catch (err: any) {
			setError(err?.apiMessage || 'Could not load your purchase orders.');
		} finally {
			setLoading(false);
		}
	}, [page, search]);

	useEffect(() => {
		load();
	}, [load]);

	if (error) return <div className='vp-alert vp-alert-error'>{error}</div>;

	return (
		<div className='vp-card'>
			<div className='vp-card-head'>
				<h3>Purchase Orders</h3>
				<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
					<input
						className='vp-input vp-input-sm'
						style={{ width: 180 }}
						placeholder='Search PO no'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
					/>
					<button
						type='button'
						className='vp-btn vp-btn-ghost vp-input-sm'
						onClick={() => setPage(1)}>
						Search
					</button>
				</div>
			</div>
			{loading ? (
				<VpSpinner />
			) : rows.length === 0 ? (
				<VpEmpty icon='purchaseOrders' text='No approved purchase orders yet.' />
			) : (
				<>
					<div className='vp-table-wrap'>
						<table className='vp-table'>
							<thead>
								<tr>
									<th>PO No</th>
									<th>Company</th>
									<th>Date</th>
									<th>Expected Delivery</th>
									<th style={{ textAlign: 'right' }}>Amount</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((po) => (
									<tr
										key={po._id}
										style={{ cursor: 'pointer' }}
										onClick={() =>
											navigate(`/vendor-portal/purchase-orders/${po._id}`)
										}>
										<td style={{ fontWeight: 600 }}>{po.purchase_order_no}</td>
										<td>{po.company?.name || '—'}</td>
										<td>{fmtDate(po.date || po.createdAt)}</td>
										<td>
											{po.expected_delivery_date
												? fmtDate(po.expected_delivery_date)
												: '—'}
										</td>
										<td style={{ textAlign: 'right' }}>
											{money(po.amount_total)}
										</td>
										<td>
											<span className='vp-badge vp-badge-confirmed'>
												Approved
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<VpPagination page={page} perPage={PER_PAGE} total={total} onPage={setPage} />
				</>
			)}
		</div>
	);
};

export default VendorPurchaseOrders;
